import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { portalApi } from '../lib/api';
import { queryKeys } from './queryKeys';

// ─── Queries ─────────────────────────────────────────────────────────────────

export function usePortalDashboard() {
  return useQuery({
    queryKey: queryKeys.portal.dashboard,
    queryFn: () => portalApi.dashboard(),
  });
}

export function usePortalProjects() {
  return useQuery({
    queryKey: queryKeys.portal.projects,
    queryFn: () => portalApi.projects(),
  });
}

export function usePortalProject(id: number) {
  return useQuery({
    queryKey: queryKeys.portal.project(id),
    queryFn: () => portalApi.project(id),
    enabled: id > 0,
  });
}

export function usePortalProposal(id: number) {
  return useQuery({
    queryKey: queryKeys.portal.proposal(id),
    queryFn: () => portalApi.proposal(id),
    enabled: id > 0,
  });
}

export function usePortalPayments() {
  return useQuery({
    queryKey: queryKeys.portal.payments,
    queryFn: () => portalApi.payments(),
  });
}

export function usePortalNotifications() {
  return useQuery({
    queryKey: queryKeys.portal.notifications,
    queryFn: () => portalApi.notifications(),
  });
}

export function useCheckoutSessionStatus(sessionId: string) {
  return useQuery({
    queryKey: queryKeys.portal.sessionStatus(sessionId),
    queryFn: () => portalApi.sessionStatus(sessionId),
    enabled: !!sessionId,
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function usePortalProjectFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, message }: { id: number; message: string }) =>
      portalApi.projectFeedback(id, message),
    onSuccess: (_d, { id }) => qc.invalidateQueries({ queryKey: queryKeys.portal.project(id) }),
  });
}

export function useApplyDiscountCode() {
  return useMutation({
    mutationFn: ({ proposalId, code }: { proposalId: number; code: string }) =>
      portalApi.applyDiscountCode(proposalId, code),
  });
}

export function useAcceptProposal() {
  return useMutation({
    mutationFn: ({ proposalId, data }: { proposalId: number; data: { payment_plan: string; installment_months?: number; installment_frequency?: string; installment_amount?: number; interest_total?: number } }) =>
      portalApi.acceptProposal(proposalId, data),
  });
}

export function useMarkNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids?: number[]) => portalApi.markNotificationsRead(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.portal.notifications }),
  });
}

export function useCreateCheckout() {
  return useMutation({
    mutationFn: ({ type, id }: { type: string; id: number }) =>
      portalApi.createCheckout(type, id),
  });
}
